import { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:6200'

export default function ProfileCompletionStep() {
  const { user, token, updateUser } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || ''
  })

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // Validate required fields
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setError('First name and last name are required')
      setLoading(false)
      return
    }

    try {
      const response = await fetch(`${API_URL}/api/v1/me/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update profile')
      }

      // Update user in context
      updateUser(data)
    } catch (err) {
      setError(err.message || 'Failed to update profile. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <span className="material-symbols-outlined text-primary text-4xl">person_add</span>
          </div>
          <h2 className="text-[#111418] text-3xl font-black leading-tight tracking-[-0.033em] mb-2">
            Welcome to Twinbooks!
          </h2>
          <p className="text-[#617589] text-base">
            Let's set up your profile to get started
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* First Name and Last Name Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex flex-col">
              <p className="text-[#111418] text-sm font-semibold leading-normal pb-2">
                First Name <span className="text-red-500">*</span>
              </p>
              <input
                className="form-input flex w-full resize-none overflow-hidden rounded-lg text-[#111418] border border-[#dbe0e6] bg-white h-12 px-4 placeholder:text-[#617589] text-base font-normal leading-normal focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow"
                placeholder="John"
                required
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
              />
            </label>

            <label className="flex flex-col">
              <p className="text-[#111418] text-sm font-semibold leading-normal pb-2">
                Last Name <span className="text-red-500">*</span>
              </p>
              <input
                className="form-input flex w-full resize-none overflow-hidden rounded-lg text-[#111418] border border-[#dbe0e6] bg-white h-12 px-4 placeholder:text-[#617589] text-base font-normal leading-normal focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow"
                placeholder="Doe"
                required
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
              />
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="rounded-lg bg-red-50 p-4 border border-red-200">
              <div className="flex items-start">
                <span className="material-symbols-outlined text-red-600 mr-3 text-[20px]">error</span>
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary hover:bg-primary/90 text-white font-bold rounded-lg h-12 shadow-md transition-all focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : 'Continue'}
          </button>
        </form>

        {/* Footer Note */}
        <p className="text-xs text-[#617589] text-center mt-6">
          Step 1 of 2: Complete your profile
        </p>
      </div>
    </div>
  )
}
